<template>
  <div class="wrapper">
    <div class="header_wrapper">
      <v-toolbar color="primary" dark class="position-relative">
        <v-btn icon @click="routerBack" style="z-index: 2;">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>

        <v-toolbar-title
          class="text-center position-absolute"
          style="left: 0; right: 0; pointer-events: none; width: 100%; margin-left: 0;"
        >
          {{ t('settingLanguage.title') }}
        </v-toolbar-title>

        <v-spacer></v-spacer>

        <v-btn
          @click="saveLanguage"
          :loading="btnLoading"
          text
          class="font-weight-bold"
          style="z-index: 2;"
        >
          {{ t('settingLanguage.save') }}
        </v-btn>
      </v-toolbar>
    </div>

    <div class="content_wrapper pa-4">
      <v-select
        v-model="selectedLanguage"
        :items="languages"
        item-title="label"
        item-value="value"
        color="primary"
        base-color="primary"
        variant="outlined"
        density="compact"
        hide-details
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Toast } from '@/service/SweetAlert';
import { t, Locale, LANGUAGE_STORAGE_KEY } from '@/i18n';
import { meModule } from '@/store/entityModules/me';

interface LanguageOption {
  value: Locale;
  label: string;
}

interface DataType {
  selectedLanguage: Locale;
  btnLoading: boolean;
}

export default defineComponent({
  computed: {
    languages(): LanguageOption[] {
      return [
        { value: 'en', label: this.t('settingLanguage.languages.english') },
        { value: 'km', label: this.t('settingLanguage.languages.khmer') },
        { value: 'ja', label: this.t('settingLanguage.languages.japanese') },
      ];
    },
  },

  data: (): DataType => ({
    selectedLanguage: 'ja',
    btnLoading: false,
  }),

  methods: {
    t,
    localeToApiLanguage(locale: Locale): number {
      if (locale === 'ja') return 0;
      if (locale === 'en') return 1;
      return 2;
    },
    apiLanguageToLocale(language: unknown): Locale | null {
      if (language === 0) return 'ja';
      if (language === 1) return 'en';
      if (language === 2) return 'km';
      return null;
    },
    routerBack() {
      this.$router.replace('/setting/various');
    },
    async saveLanguage() {
      const me = meModule.me;
      if (!me) return;

      this.btnLoading = true;
      try {
        await meModule.putMe({
          firstName: me.firstName,
          lastName: me.lastName,
          firstNameKana: me.firstNameKana,
          lastNameKana: me.lastNameKana,
          imageUrl: me.imageUrl,
          birth: me.birth,
          bloodType: me.bloodType,
          tel: me.tel,
          language: this.localeToApiLanguage(this.selectedLanguage),
          notifications: Object.keys(me.notifications).map((key) => ({
            parentId: me.parentId,
            key,
            value: me.notifications[key],
          })),
          isNewinfo: me.isNewinfo,
        });
        localStorage.setItem(LANGUAGE_STORAGE_KEY, this.selectedLanguage);
        const vuetifyLocale = (this as any).$vuetify?.locale;
        if (vuetifyLocale) {
          if (typeof vuetifyLocale.change === 'function') {
            vuetifyLocale.change(this.selectedLanguage);
          } else {
            vuetifyLocale.current = this.selectedLanguage;
          }
        }
        Toast.fire({
          icon: 'success',
          title: this.t('settingLanguage.successMessage'),
        });
        this.$router.replace('/setting/various');
      } finally {
        this.btnLoading = false;
      }
    },
  },

  mounted() {
    const meLanguage = this.apiLanguageToLocale((meModule.me as any)?.language);
    if (meLanguage) {
      this.selectedLanguage = meLanguage;
      return;
    }

    const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (savedLanguage === 'ja' || savedLanguage === 'en' || savedLanguage === 'km') {
      this.selectedLanguage = savedLanguage;
    }
  },
});
</script>

<style lang="scss" scoped>
.wrapper {
  padding-top: calc(var(--app-top-offset) + 56px);
  margin-bottom: calc(env(safe-area-inset-bottom) + 56px);
}

.header_wrapper {
  height: 56px;
  width: 100%;
  position: fixed;
  top: var(--app-top-offset);
  left: 0;
  z-index: 100;
}
</style>
